import React, { useState, useEffect, useMemo, useRef } from "react";
import { Row, Col, Dropdown, Form } from "react-bootstrap";
import {
  FaArrowLeft,
  FaChevronDown,
  FaExclamationTriangle,
  FaFlag,
  FaCalendarAlt,
  FaTimes,
} from "react-icons/fa";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import SendCoCMInviteModal from "../components/CoCM/SendCoCMInviteModal";
import SendGuardianAccessModal from "../components/CoCM/SendGuardianAccessModal";
import ResendCareSupportNotificationModal from "../components/CoCM/ResendCareSupportNotificationModal";
import AppAccessDetailsModal from "../components/CoCM/AppAccessDetailsModal";
import DeactivateAppAccessModal from "../components/CoCM/DeactivateAppAccessModal";
import DeactivatePatientModal from "../components/MyPatients/DeactivatePatientModal";
import { isCoCMSuperAdmin } from "../constants/cocmDeactivate";
import useClinicOptions from "../hooks/useClinicOptions";
import useDoctorOptions from "../hooks/useDoctorOptions";

const formatAppAccessDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatAppAccessDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const statusColors = {
  Active: { bg: "#E2F6EC", color: "#0F9D58" },
  Intake: { bg: "#E8F1FF", color: "#2F6FED" },
  Monitoring: { bg: "#F2ECFF", color: "#7A52CC" },
  Outreach: { bg: "#FFF4E5", color: "#D9822B" },
  Inactive: { bg: "#FEE2E2", color: "#991B1B" },
};

const formatDateToYYYYMMDD = (dateStr) => {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  const mdyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, m, d, y] = mdyMatch;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDateToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";
  const yyyymmdd = formatDateToYYYYMMDD(dateStr);
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  return `${m}/${d}/${y}`;
};

const calculateAge = (dateStr) => {
  if (!dateStr) return "";
  const yyyymmdd = formatDateToYYYYMMDD(dateStr);
  if (!yyyymmdd) return "";
  const [birthYear, birthMonth, birthDay] = yyyymmdd.split("-").map(Number);
  const today = new Date();
  let age = today.getFullYear() - birthYear;
  const monthDiff = today.getMonth() + 1 - birthMonth;
  const dayDiff = today.getDate() - birthDay;
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }
  return age >= 0 ? age : 0;
};

const mapCoCMPatientFromApi = (user) => ({
  id: user._id,
  patientId: user.cocmMeta?.patientId || "",
  name: `${user.firstname} ${user.lastname}`,
  dob: formatDateToYYYYMMDD(user.dob) || "",
  email: user.email || "",
  phone: user.phoneno || "",
  status: user.cocmMeta?.status || "Intake",
  clinic: user.cocmMeta?.clinic || "",
  pcp: user.cocmMeta?.pcp || "",
  cm: user.cocmMeta?.careManager || "",
  cmId: user.cocmMeta?.careManagerId || "",
  psychiatricConsultant: user.cocmMeta?.psychiatricConsultant || "",
  psychiatricConsultantId: user.cocmMeta?.psychiatricConsultantId || "",
  enrollmentDate: user.cocmMeta?.enrollmentDate || "",
  dischargeDate: user.cocmMeta?.dischargeDate || "",
  inactiveReason: user.cocmMeta?.inactiveReason || "",
  inactiveDate: user.cocmMeta?.inactiveDate || "",
  inactiveBy: user.cocmMeta?.inactiveBy || null,
  initialAssessment: user.cocmMeta?.initialAssessment || "",
  lastFollowUp: user.cocmMeta?.lastFollowUp || "",
  nextFollowUp: user.cocmMeta?.nextFollowUp
    ? formatDateToYYYYMMDD(user.cocmMeta.nextFollowUp)
    : "",
  lastCaseReview: user.cocmMeta?.lastCaseReview || "",
  nextCaseReview: user.cocmMeta?.nextCaseReview
    ? formatDateToYYYYMMDD(user.cocmMeta.nextCaseReview)
    : "",
  phq9: user.cocmMeta?.phq9 || { initial: null, latest: null },
  gad7: user.cocmMeta?.gad7 || { initial: null, latest: null },
  smfq: user.cocmMeta?.smfq || { initial: null, latest: null },
  scared: user.cocmMeta?.scared || { initial: null, latest: null },
  minutes: user.cocmMeta?.minutes || 0,
  flags: user.cocmMeta?.flags || { flag: false, warning: false },
  numenteAccess: String(user.cocmMeta?.numenteAccess || "")
    .toLowerCase()
    .trim(),
});

const parseScore = (val) => {
  if (val === "-" || val === null || val === undefined || val === "") return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

const computeAssessmentScores = (activities, scoreField) => {
  const sorted = [...activities]
    .filter((a) => parseScore(a[scoreField]) !== null)
    .sort((a, b) => (a.dateMs || 0) - (b.dateMs || 0));
  if (!sorted.length) return { initial: null, latest: null };
  return {
    initial: parseScore(sorted[0][scoreField]),
    latest: parseScore(sorted[sorted.length - 1][scoreField]),
  };
};

const latestActivityDateByType = (activities, types) => {
  const typeList = Array.isArray(types) ? types : [types];
  const matched = activities.filter((a) => typeList.includes(a.activityType));
  if (!matched.length) return "";
  const latest = matched.reduce((best, a) =>
    (a.dateMs || 0) > (best.dateMs || 0) ? a : best,
  );
  return latest.date || "";
};

const daysSinceDate = (dateStr) => {
  const ymd = formatDateToYYYYMMDD(dateStr);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const then = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  then.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - then) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : 0;
};

const getMostRecentContactDate = (...dateValues) => {
  let maxMs = 0;
  let maxFormatted = "";
  dateValues.forEach((val) => {
    if (!val) return;
    const ymd = formatDateToYYYYMMDD(val);
    if (!ymd) return;
    const ms = new Date(ymd).getTime();
    if (ms > maxMs) {
      maxMs = ms;
      maxFormatted = formatDateToMMDDYYYY(val);
    }
  });
  return maxFormatted;
};

const ClickUpDatePicker = ({ value, onChange, inline = false }) => {
  const [hovered, setHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const checkIfOverdue = (dateString) => {
    if (!dateString) return false;
    const ymd = formatDateToYYYYMMDD(dateString);
    if (!ymd) return false;
    const [y, m, d] = ymd.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isOverdue = checkIfOverdue(value);

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("");
  };

  const handleContainerClick = (e) => {
    if (e.target.closest(".clear-btn")) return;
    if (inputRef.current) {
      try {
        inputRef.current.showPicker();
      } catch {
        inputRef.current.focus();
      }
    }
  };

  const formattedDate = value ? formatDateToMMDDYYYY(value) : "";

  const baseStyle = inline
    ? {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        padding: "0",
        borderRadius: "6px",
        backgroundColor: isFocused
          ? "#E6F0FF"
          : hovered
            ? "#E6F0FF"
            : "transparent",
        border: isFocused
          ? "1px solid #2F6FED"
          : hovered
            ? "1px solid #B3D4FF"
            : "1px solid transparent",
        cursor: "pointer",
        minHeight: "22px",
        transition: "all 0.2s ease",
        userSelect: "none",
      }
    : {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 10px",
        borderRadius: "6px",
        backgroundColor: isFocused
          ? "#E6F0FF"
          : value
            ? hovered
              ? "#E6F0FF"
              : "transparent"
            : hovered
              ? "#E6F0FF"
              : "#E9EEF1",
        border: isFocused
          ? "1px solid #2F6FED"
          : value && hovered
            ? "1px solid #B3D4FF"
            : "1px solid transparent",
        cursor: "pointer",
        minHeight: "28px",
        minWidth: "110px",
        transition: "all 0.2s ease",
        userSelect: "none",
      };

  return (
    <div
      onClick={handleContainerClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={baseStyle}
    >
      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          zIndex: 2,
        }}
      />
      {value ? (
        <span
          style={{
            color: isOverdue ? "#D64545" : inline ? "#333333" : "#064260",
            fontWeight: "700",
            fontSize: inline ? "14px" : "14px",
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            width: inline ? "auto" : "100%",
            justifyContent: inline ? "flex-start" : "space-between",
          }}
        >
          <span>{formattedDate}</span>
          {hovered && (
            <button
              className="clear-btn"
              onClick={handleClear}
              style={{
                background: "none",
                border: "none",
                padding: "2px",
                color: "#8E8E93",
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                zIndex: 3,
              }}
            >
              {inline ? (
                <FaCalendarAlt size={11} style={{ color: "#27B99A" }} />
              ) : (
                <FaTimes size={10} />
              )}
            </button>
          )}
        </span>
      ) : (
        <span
          style={{
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            color: hovered ? "#27B99A" : inline ? "#333333" : "#7F8C8D",
            fontWeight: inline ? "700" : "400",
            fontSize: inline ? "14px" : "12px",
          }}
        >
          {hovered ? (
            <FaCalendarAlt size={12} style={{ color: "#27B99A" }} />
          ) : inline ? (
            "—"
          ) : (
            <span style={{ fontSize: "12px", color: "#A0AEC0" }}>Set Date</span>
          )}
        </span>
      )}
    </div>
  );
};

const PatientInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const user = useSelector((state) => state?.user?.user);
  const canDeactivatePatients = isCoCMSuperAdmin(user);
  const { clinics: clinicOptions } = useClinicOptions(false);

  const [activeTab, setActiveTab] = useState("Overview");
  const [patientActivities, setPatientActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    const container = document.querySelector(".container-fluid");

    let originalPadding = "";
    let originalDisplay = "";
    let originalFlexDir = "";
    let originalMinHeight = "";
    let originalFlex = "";
    let originalHeight = "";
    let containerBg = "";

    // 1. Style .container-fluid
    if (container) {
      originalPadding = container.style.padding;
      originalDisplay = container.style.display;
      originalFlexDir = container.style.flexDirection;
      originalMinHeight = container.style.minHeight;
      originalFlex = container.style.flex;
      originalHeight = container.style.height;
      containerBg = container.style.backgroundColor;

      container.style.setProperty("padding", "0", "important");
      container.style.setProperty("display", "flex", "important");
      container.style.setProperty("flex-direction", "row", "important");
      container.style.setProperty("flex", "1", "important");
      container.style.setProperty("min-height", "100%", "important");
      container.style.setProperty("height", "auto", "important");
      container.style.setProperty("background-color", "#F7F7F7", "important");
    }

    return () => {
      if (container) {
        if (originalPadding)
          container.style.setProperty("padding", originalPadding);
        else container.style.removeProperty("padding");

        if (originalDisplay)
          container.style.setProperty("display", originalDisplay);
        else container.style.removeProperty("display");

        if (originalFlexDir)
          container.style.setProperty("flex-direction", originalFlexDir);
        else container.style.removeProperty("flex-direction");

        if (originalMinHeight)
          container.style.setProperty("min-height", originalMinHeight);
        else container.style.removeProperty("min-height");

        if (originalFlex) container.style.setProperty("flex", originalFlex);
        else container.style.removeProperty("flex");

        if (originalHeight)
          container.style.setProperty("height", originalHeight);
        else container.style.removeProperty("height");

        if (containerBg)
          container.style.setProperty("background-color", containerBg);
        else container.style.removeProperty("background-color");
      }
    };
  }, []);

  const [currentPatient, setCurrentPatient] = useState(null);
  const [appAccess, setAppAccess] = useState(null);
  const [guardianAccess, setGuardianAccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [careManagers, setCareManagers] = useState([]);
  const [psychiatricConsultants, setPsychiatricConsultants] = useState([]);
  const [showSendInviteModal, setShowSendInviteModal] = useState(false);
  const [showSendGuardianModal, setShowSendGuardianModal] = useState(false);
  const [showResendCareSupportModal, setShowResendCareSupportModal] =
    useState(false);
  const [showAppAccessDetailsModal, setShowAppAccessDetailsModal] = useState(false);
  const [showDeactivateAppAccessModal, setShowDeactivateAppAccessModal] = useState(false);
  const [showDeactivatePatientModal, setShowDeactivatePatientModal] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [guardianInviteSubmitting, setGuardianInviteSubmitting] =
    useState(false);
  const [careSupportResendSubmitting, setCareSupportResendSubmitting] =
    useState(false);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [patientDeactivating, setPatientDeactivating] = useState(false);

  // Decode role from stored JWT
  const _token = localStorage.getItem("user_id");
  let _userRole = null;
  try {
    if (_token) {
      const payload = JSON.parse(atob(_token.split(".")[1]));
      _userRole = payload?.role || null;
    }
  } catch (_) {}

  const handleBack = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    if (_userRole === "careManager") {
      navigate("/careManager/my-patients");
      return;
    }
    if (_userRole === "psychiatricConsultant") {
      navigate("/psychiatricConsultant/my-patients");
      return;
    }
    navigate("/registry-cocm");
  };

  const [editFormData, setEditFormData] = useState({
    patientId: "",
    firstName: "",
    lastName: "",
    dob: "",
    phone: "",
    email: "",
    status: "",
    enrollmentDate: "",
    clinic: "",
    pcp: "",
    careManager: "",
    careManagerId: "",
    psychiatricConsultant: "",
    psychiatricConsultantId: "",
  });
  const { doctors: doctorOptions } = useDoctorOptions(
    true,
    editFormData.clinic || currentPatient?.clinic || "",
  );

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem("user_id");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/get-cocm-staff`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.data?.success) {
          setCareManagers(response.data.data.careManagers || []);
          setPsychiatricConsultants(
            response.data.data.psychiatricConsultants || [],
          );
        }
      } catch (error) {
        console.error("Error fetching staff list:", error);
      }
    };
    fetchStaff();
  }, []);

  const fetchPatientDetails = async () => {
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/get-patient/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        const user = response.data.data;
        const mapped = mapCoCMPatientFromApi(user);
        setCurrentPatient(mapped);
        setAppAccess(user.appAccess || null);
        setGuardianAccess(user.guardianAccess || null);
        setCurrentStatus(mapped.status);
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
    }
  };

  const handleSendInvite = async () => {
    if (!id) return;
    setInviteSubmitting(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${id}/send-invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setAppAccess(response.data.data?.appAccess || null);
        setShowSendInviteModal(false);
        await fetchPatientDetails();
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      alert(error.response?.data?.message || "Failed to send invite");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleSendGuardianInvite = async (form) => {
    if (!id) return;
    setGuardianInviteSubmitting(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${id}/send-guardian-invite`,
        {
          email: form?.email,
          firstName: form?.firstName,
          lastName: form?.lastName,
          phone: form?.phone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setGuardianAccess(response.data.data?.guardianAccess || null);
        setShowSendGuardianModal(false);
        await fetchPatientDetails();
      }
    } catch (error) {
      console.error("Error sending guardian email:", error);
      alert(error.response?.data?.message || "Failed to send guardian email");
    } finally {
      setGuardianInviteSubmitting(false);
    }
  };

  const handleResendCareSupportNotification = async () => {
    if (!id) return;
    setCareSupportResendSubmitting(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${id}/resend-care-support-notification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.data?.appAccess) {
        setAppAccess(response.data.data.appAccess);
      }
      if (response.data?.success) {
        setShowResendCareSupportModal(false);
        await fetchPatientDetails();
        await fetchPatientActivities();
      } else {
        alert(
          response.data?.message || "Failed to resend care support notification",
        );
      }
    } catch (error) {
      console.error("Error resending care support notification:", error);
      if (error.response?.data?.data?.appAccess) {
        setAppAccess(error.response.data.data.appAccess);
      }
      alert(
        error.response?.data?.message ||
          "Failed to resend care support notification",
      );
    } finally {
      setCareSupportResendSubmitting(false);
    }
  };

  const handleDeactivateAppAccess = async () => {
    if (!id) return;
    setDeactivateSubmitting(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${id}/deactivate-app-access`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setAppAccess(response.data.data?.appAccess || null);
        setShowDeactivateAppAccessModal(false);
        await fetchPatientDetails();
      }
    } catch (error) {
      console.error("Error deactivating app access:", error);
      alert(error.response?.data?.message || "Failed to deactivate app access");
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  const fetchPatientActivities = async () => {
    if (!id) return;
    try {
      setLoadingActivities(true);
      const token = localStorage.getItem("user_id");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/cocm-activities?patientId=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        setPatientActivities(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching patient activities:", error);
      setPatientActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  useEffect(() => {
    fetchPatientDetails();
    fetchPatientActivities();
  }, [id]);

  const patientActivityNotes = patientActivities.filter(
    (act) => act.note && act.note.trim(),
  );

  const isAdmin = _userRole === "admin" || _userRole === "business";
  const usePatientMetaStats = isAdmin;

  const overviewStats = useMemo(() => {
    if (!currentPatient) return null;

    const p = currentPatient;
    const activities = patientActivities;

    const buildFromMeta = (field) => ({
      initial: parseScore(p[field]?.initial),
      latest: parseScore(p[field]?.latest),
    });

    let assessments;
    let engagement;

    if (usePatientMetaStats) {
      assessments = {
        phq9: buildFromMeta("phq9"),
        gad7: buildFromMeta("gad7"),
        smfq: buildFromMeta("smfq"),
        scared: buildFromMeta("scared"),
      };

      const activityDates = activities.map((a) => a.date);
      const lastContact = getMostRecentContactDate(
        p.enrollmentDate,
        p.initialAssessment,
        p.lastFollowUp,
        p.lastCaseReview,
        ...activityDates,
      );

      engagement = {
        enrollmentDate: p.enrollmentDate
          ? formatDateToMMDDYYYY(p.enrollmentDate)
          : "",
        initialAssessmentDate: p.initialAssessment
          ? formatDateToMMDDYYYY(p.initialAssessment)
          : "",
        dischargeDate: p.dischargeDate
          ? formatDateToMMDDYYYY(p.dischargeDate)
          : "",
        lastFollowUp: p.lastFollowUp
          ? formatDateToMMDDYYYY(p.lastFollowUp)
          : "",
        nextFollowUp: p.nextFollowUp || "",
        lastCaseReview: p.lastCaseReview
          ? formatDateToMMDDYYYY(p.lastCaseReview)
          : "",
        nextCaseReview: p.nextCaseReview || "",
        daysSince: daysSinceDate(lastContact),
      };
    } else {
      assessments = {
        phq9: computeAssessmentScores(activities, "phq9"),
        gad7: computeAssessmentScores(activities, "gad7"),
        smfq: computeAssessmentScores(activities, "smfq"),
        scared: computeAssessmentScores(activities, "scared"),
      };

      const lastFollowUpFromActs = latestActivityDateByType(
        activities,
        "Follow-Up",
      );
      const lastCaseReviewFromActs = latestActivityDateByType(
        activities,
        "Case Review",
      );
      const initialAssessmentFromActs = latestActivityDateByType(
        activities,
        "Initial Assessment",
      );

      const lastContact = getMostRecentContactDate(
        p.enrollmentDate,
        initialAssessmentFromActs,
        lastFollowUpFromActs,
        lastCaseReviewFromActs,
        ...activities.map((a) => a.date),
      );

      engagement = {
        enrollmentDate: p.enrollmentDate
          ? formatDateToMMDDYYYY(p.enrollmentDate)
          : "",
        initialAssessmentDate: initialAssessmentFromActs
          || (p.initialAssessment ? formatDateToMMDDYYYY(p.initialAssessment) : ""),
        dischargeDate: p.dischargeDate
          ? formatDateToMMDDYYYY(p.dischargeDate)
          : "",
        lastFollowUp: lastFollowUpFromActs
          || (p.lastFollowUp ? formatDateToMMDDYYYY(p.lastFollowUp) : ""),
        nextFollowUp: p.nextFollowUp || "",
        lastCaseReview: lastCaseReviewFromActs
          || (p.lastCaseReview ? formatDateToMMDDYYYY(p.lastCaseReview) : ""),
        nextCaseReview: p.nextCaseReview || "",
        daysSince: daysSinceDate(lastContact),
      };
    }

    return { assessments, engagement };
  }, [currentPatient, patientActivities, usePatientMetaStats]);

  const handleEngagementDateChange = async (field, newValue) => {
    if (currentPatient?.status === "Inactive") return;
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        { [field]: newValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        setCurrentPatient((prev) =>
          prev ? { ...prev, [field]: newValue } : prev,
        );
      }
    } catch (error) {
      console.error("Error updating date:", error);
    }
  };

  const [currentStatus, setCurrentStatus] = useState("Intake");
  const currentSColor = statusColors[currentStatus] || statusColors.Active;

  const handleStartEdit = () => {
    if (!currentPatient || currentPatient.status === "Inactive") return;
    setEditFormData({
      patientId: currentPatient.patientId,
      firstName: currentPatient.name.split(" ")[0] || "",
      lastName: currentPatient.name.split(" ").slice(1).join(" ") || "",
      dob: currentPatient.dob,
      phone: currentPatient.phone,
      email: currentPatient.email,
      status: currentPatient.status,
      enrollmentDate: currentPatient.enrollmentDate,
      clinic: currentPatient.clinic,
      pcp: currentPatient.pcp,
      careManager: currentPatient.cm,
      careManagerId: currentPatient.cmId,
      psychiatricConsultant: currentPatient.psychiatricConsultant,
      psychiatricConsultantId: currentPatient.psychiatricConsultantId || "",
    });
    setIsEditing(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCareManagerChange = (e) => {
    const selectedId = e.target.value;
    const selectedCM = careManagers.find(
      (cm) => cm.id.toString() === selectedId,
    );
    setEditFormData((prev) => ({
      ...prev,
      careManagerId: selectedId,
      careManager: selectedCM ? selectedCM.userName : "",
    }));
  };

  const handlePsychiatricConsultantChange = (e) => {
    const selectedId = e.target.value;
    const selectedPC = psychiatricConsultants.find(
      (pc) => pc.id.toString() === selectedId,
    );
    setEditFormData((prev) => ({
      ...prev,
      psychiatricConsultantId: selectedId,
      psychiatricConsultant: selectedPC ? selectedPC.userName : "",
    }));
  };

  const handleSaveChanges = async (e) => {
    if (e) e.preventDefault();
    if (currentPatient?.status === "Inactive") return;
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        editFormData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        const user = response.data.data;
        const mapped = mapCoCMPatientFromApi(user);
        setCurrentPatient(mapped);
        setCurrentStatus(mapped.status);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Error saving edits:", error);
      alert(error.response?.data?.message || "Failed to save edits");
    }
  };

  const handleDeactivatePatient = async ({
    inactiveReason,
    inactiveReasonOther,
    keepAppAccess,
  }) => {
    if (!id) return;

    setPatientDeactivating(true);
    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/cocm-patient/${id}/deactivate`,
        {
          inactiveReason,
          inactiveReasonOther,
          keepAppAccess,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data?.success) {
        setShowDeactivatePatientModal(false);
        await fetchPatientDetails();
        await fetchPatientActivities();
      }
    } catch (error) {
      console.error("Error deactivating patient:", error);
      alert(
        error.response?.data?.message ||
          "Failed to deactivate patient. Please try again.",
      );
    } finally {
      setPatientDeactivating(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "Inactive") {
      setShowDeactivatePatientModal(true);
      return;
    }

    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success) {
        const user = response.data.data;
        const mapped = mapCoCMPatientFromApi(user);
        setCurrentPatient(mapped);
        setCurrentStatus(mapped.status);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleToggleFlag = async (field) => {
    if (!currentPatient || currentPatient.status === "Inactive") return;

    const updatedFlags = {
      ...currentPatient.flags,
      [field]: !currentPatient.flags[field],
    };

    try {
      const token = localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/edit-patient/${id}`,
        { flags: updatedFlags },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data?.success) {
        const user = response.data.data;
        const mapped = mapCoCMPatientFromApi(user);
        setCurrentPatient(mapped);
        setCurrentStatus(mapped.status);
      }
    } catch (error) {
      console.error("Error updating flag:", error);
      alert(error.response?.data?.message || "Failed to update flag");
    }
  };

  if (!currentPatient) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "calc(100vh - 86px)",
          fontFamily: "Poppins",
        }}
      >
        Loading patient details...
      </div>
    );
  }

  const patient = currentPatient;
  const isPatientInactive = patient.status === "Inactive";
  const initials = patient.name
    .split(" ")
    .map((n) => n[0])
    .join("");
  const patientAge = calculateAge(patient.dob);
  const isMinor = typeof patientAge === "number" && patientAge < 18;
  const accessMode = String(
    patient.numenteAccess || (isMinor ? "guardian" : "patient"),
  )
    .toLowerCase()
    .trim();
  const wantsPatientAccess =
    !isMinor || accessMode === "patient" || accessMode === "both";
  const hasGuardianRecord =
    Boolean(guardianAccess?.email) ||
    Boolean(guardianAccess?.thisChildLinked) ||
    (Boolean(guardianAccess?.status) &&
      guardianAccess.status !== "Not Linked");
  const hasActiveAppAccess =
    Boolean(appAccess?.status) && appAccess.status !== "Not Invited";
  // Match Add New Patient: guardian section is for minors (or legacy guardian data).
  const showGuardianAccessSection =
    _userRole === "admin" && (isMinor || hasGuardianRecord);
  // Patient app access only when patient access applies, or an invite/link already exists.
  const showAppAccessSection =
    _userRole === "admin" &&
    (wantsPatientAccess || Boolean(patient.email) || hasActiveAppAccess);

  const renderAssessmentCard = (title, initial, latest) => {
    const initNum = parseScore(initial);
    const latestNum = parseScore(latest);
    let changeText = "—";
    let changeColor = "#8E8E93";

    if (initNum !== null && latestNum !== null) {
      const diff = initNum - latestNum;
      if (diff > 0) {
        changeText = `↓ ${diff}`;
        changeColor = "#27B99A";
      } else if (diff < 0) {
        changeText = `↑ ${Math.abs(diff)}`;
        changeColor = "#D64545";
      } else {
        changeText = "0";
        changeColor = "#8E8E93";
      }
    }

    const displayInitial = initNum !== null ? initNum : "—";
    const displayLatest = latestNum !== null ? latestNum : "—";

    return (
      <Col md={6}>
        <div
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #EAECEF",
            borderRadius: "12px",
            padding: "18px 20px",
            height: "100%",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#064260",
              marginBottom: "16px",
            }}
          >
            {title}
          </div>
          <Row className="g-0">
            <Col xs={4}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8E8E93",
                  fontWeight: "600",
                  letterSpacing: "0.3px",
                }}
              >
                Initial
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#333333",
                  marginTop: "6px",
                  lineHeight: 1.2,
                }}
              >
                {displayInitial}
              </div>
            </Col>
            <Col xs={4}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8E8E93",
                  fontWeight: "600",
                  letterSpacing: "0.3px",
                }}
              >
                Latest
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#333333",
                  marginTop: "6px",
                  lineHeight: 1.2,
                }}
              >
                {displayLatest}
              </div>
            </Col>
            <Col xs={4}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8E8E93",
                  fontWeight: "600",
                  letterSpacing: "0.3px",
                }}
              >
                Change
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: changeColor,
                  marginTop: "6px",
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {changeText}
              </div>
            </Col>
          </Row>
        </div>
      </Col>
    );
  };

  const renderEngagementItem = (label, value) => (
    <Col md={3} xs={6} className="mb-4">
      <div
        style={{
          fontSize: "12px",
          color: "#8E8E93",
          marginBottom: "8px",
          fontWeight: "500",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "#333333",
          lineHeight: 1.3,
        }}
      >
        {value ?? "—"}
      </div>
    </Col>
  );

  const renderEditableEngagementItem = (label, field, value) => (
    <Col md={3} xs={6} className="mb-4">
      <div
        style={{
          fontSize: "12px",
          color: "#8E8E93",
          marginBottom: "8px",
          fontWeight: "500",
        }}
      >
        {label}
      </div>
      <ClickUpDatePicker
        inline
        value={value || ""}
        onChange={(newVal) => handleEngagementDateChange(field, newVal)}
      />
    </Col>
  );

  const stats = overviewStats;

  return (
    <>
      <style>{`
        .custom-dropdown-toggle::after { display: none !important; }
      `}</style>

      {isEditing && !isPatientInactive ? (
        <div
          style={{
            flex: 1,
            backgroundColor: "#F7F7F7",
            minHeight: "calc(100vh - 86px)",
            padding: "16px 20px 16px 0",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              width: "540px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #EAECEF",
              borderRadius: "10px",
              padding: "40px 32px",
              overflowY: "auto",
            }}
          >
            <Form onSubmit={handleSaveChanges}>
              <h2
                className="mb-5"
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#064260",
                }}
              >
                Edit Patient
              </h2>

              {/* Patient ID */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Patient ID
                </Form.Label>
                <Form.Control
                  type="text"
                  name="patientId"
                  value={editFormData.patientId}
                  disabled
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* First Name */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  First Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={editFormData.firstName}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Last Name */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Last Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={editFormData.lastName}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Date of Birth */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Date of Birth
                </Form.Label>
                <Form.Control
                  type="date"
                  name="dob"
                  value={editFormData.dob}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Phone Number */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Phone Number
                </Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Email */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Status */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Status
                </Form.Label>
                <Form.Select
                  name="status"
                  value={editFormData.status}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  <option value="Intake">Intake</option>
                  <option value="Active">Active</option>
                  <option value="Monitoring">Monitoring</option>
                  <option value="Outreach">Outreach</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>

              {/* Enrollment Date */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Enrollment Date
                </Form.Label>
                <Form.Control
                  type="date"
                  name="enrollmentDate"
                  value={editFormData.enrollmentDate}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                />
              </Form.Group>

              {/* Clinic */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Clinic
                </Form.Label>
                <Form.Select
                  name="clinic"
                  value={editFormData.clinic}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  <option value="">Select Clinic</option>
                  {clinicOptions.map((clinic) => (
                    <option key={clinic.id} value={clinic.name}>
                      {clinic.name}
                    </option>
                  ))}
                  {editFormData.clinic &&
                    !clinicOptions.some((c) => c.name === editFormData.clinic) && (
                      <option value={editFormData.clinic}>
                        {editFormData.clinic}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>

              {/* PCP */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  PCP
                </Form.Label>
                <Form.Select
                  name="pcp"
                  value={editFormData.pcp}
                  onChange={handleEditChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  <option value="">Select PCP</option>
                  {doctorOptions.map((doctor) => (
                    <option key={doctor.id} value={doctor.name}>
                      {doctor.name}
                    </option>
                  ))}
                  {editFormData.pcp &&
                    !doctorOptions.some((d) => d.name === editFormData.pcp) && (
                      <option value={editFormData.pcp}>
                        {editFormData.pcp}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>

              {/* Care Manager */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Care Manager
                </Form.Label>
                <Form.Select
                  name="careManagerId"
                  value={editFormData.careManagerId || ""}
                  onChange={handleCareManagerChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  <option value="">Select Care Manager</option>
                  {careManagers.map((cm) => (
                    <option key={cm.id} value={cm.id}>
                      {cm.userName}
                    </option>
                  ))}
                  {editFormData.careManager &&
                    !careManagers.some(
                      (cm) => cm.id.toString() === editFormData.careManagerId,
                    ) && (
                      <option value={editFormData.careManagerId}>
                        {editFormData.careManager}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>

              {/* Psychiatric Consultant */}
              <Form.Group className="mb-4">
                <Form.Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#333333",
                    marginBottom: "8px",
                  }}
                >
                  Psychiatric Consultant
                </Form.Label>
                <Form.Select
                  name="psychiatricConsultantId"
                  value={editFormData.psychiatricConsultantId || ""}
                  onChange={handlePsychiatricConsultantChange}
                  style={{
                    backgroundColor: "#F8F9FA",
                    border: "none",
                    borderRadius: "8px",
                    height: "48px",
                    padding: "0 16px",
                    fontSize: "14px",
                    color: "#444444",
                  }}
                >
                  <option value="">Select Psychiatric Consultant</option>
                  {psychiatricConsultants.map((pc) => (
                    <option key={pc.id} value={pc.id}>
                      {pc.userName}
                    </option>
                  ))}
                  {editFormData.psychiatricConsultant &&
                    !psychiatricConsultants.some(
                      (pc) =>
                        pc.id.toString() ===
                        editFormData.psychiatricConsultantId,
                    ) && (
                      <option value={editFormData.psychiatricConsultantId}>
                        {editFormData.psychiatricConsultant}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "16px", marginTop: "40px" }}>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  style={{
                    flex: 1,
                    height: "48px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#E9EEF1",
                    color: "#064260",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    height: "48px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#27B99A",
                    color: "#FFFFFF",
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Save Changes
                </button>
              </div>
            </Form>
          </div>
          <div style={{ flex: 1 }} />
        </div>
      ) : (
        <>
          {/* ─── Left Sidebar Panel ─── */}
          <div
            style={{
              width: "320px",
              backgroundColor: "#F7F7F7",
              padding: "16px 0 16px 0",
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              minHeight: "calc(100vh - 86px)",
            }}
          >
            <div
              style={{
                backgroundColor: "#FFFFFF",
                padding: "10px 24px",
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              {/* Top bar with Back and Edit */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "32px",
                }}
              >
                <div
                  onClick={handleBack}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "#27B99A",
                    fontWeight: "600",
                    fontSize: "14px",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#27B99A",
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    <FaArrowLeft size={10} />
                  </div>
                  Back
                </div>
                {_userRole === "admin" && !isPatientInactive && (
                  <span
                    onClick={handleStartEdit}
                    style={{
                      color: "#27B99A",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </span>
                )}
              </div>

              {/* Avatar and Name details */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "50%",
                    backgroundColor: "#006C9C",
                    color: "#FFFFFF",
                    fontSize: "24px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  {initials}
                </div>
                <h4
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#064260",
                    margin: "0 0 6px 0",
                  }}
                >
                  {patient.name}
                </h4>
                <span style={{ fontSize: "12px", color: "#8E8E93" }}>
                  DOB: {formatDateToMMDDYYYY(patient.dob)} • Age{" "}
                  {calculateAge(patient.dob)} • ID #{patient.patientId}
                </span>

                {patient.status === "Inactive" && (
                  <div
                    style={{
                      width: "100%",
                      marginTop: "16px",
                      padding: "12px 14px",
                      backgroundColor: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: "8px",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                      }}
                    >
                      <FaExclamationTriangle size={14} color="#DC2626" />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#991B1B",
                        }}
                      >
                        Patient Inactive
                      </span>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#7F1D1D",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Reason:</strong> {patient.inactiveReason || "—"}
                      <br />
                      <strong>Date:</strong>{" "}
                      {patient.inactiveDate
                        ? formatDateToMMDDYYYY(patient.inactiveDate)
                        : "—"}
                      <br />
                      <strong>By:</strong>{" "}
                      {patient.inactiveBy?.name || "—"}
                    </p>
                  </div>
                )}

                {/* Status Dropdown */}
                <div style={{ width: "100%", marginTop: "18px" }}>
                  <Dropdown onSelect={handleStatusChange}>
                    <Dropdown.Toggle
                      className="custom-dropdown-toggle"
                      style={{
                        backgroundColor: currentSColor.bg,
                        border: "none",
                        color: currentSColor.color,
                        borderRadius: "6px",
                        padding: "8px 16px",
                        fontSize: "13px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        textAlign: "left",
                        boxShadow: "none",
                      }}
                    >
                      <span>{currentStatus}</span>
                      <FaChevronDown size={10} style={{ opacity: 0.7 }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ width: "100%", fontSize: "13px" }}>
                      <Dropdown.Item eventKey="Intake">Intake</Dropdown.Item>
                      <Dropdown.Item eventKey="Active">Active</Dropdown.Item>
                      <Dropdown.Item eventKey="Monitoring">
                        Monitoring
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="Outreach">
                        Outreach
                      </Dropdown.Item>
                      {canDeactivatePatients && (
                        <Dropdown.Item eventKey="Inactive">
                          Inactive
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {canDeactivatePatients && patient.status !== "Inactive" && (
                  <button
                    type="button"
                    onClick={() => setShowDeactivatePatientModal(true)}
                    style={{
                      width: "100%",
                      marginTop: "12px",
                      height: "38px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #EF4444",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#EF4444",
                      cursor: "pointer",
                    }}
                  >
                    Deactivate Patient
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div
                style={{ display: "flex", gap: "8px", marginBottom: "28px" }}
              >
                <button
                  type="button"
                  onClick={
                    isPatientInactive
                      ? undefined
                      : () => handleToggleFlag("flag")
                  }
                  disabled={isPatientInactive}
                  style={{
                    flex: 1,
                    height: "36px",
                    backgroundColor: patient.flags.flag ? "#FEF9E7" : "#FFFFFF",
                    border: patient.flags.flag
                      ? "1px solid #F9E79F"
                      : "1px solid #EAECEF",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: patient.flags.flag ? "#C29600" : "#8E8E93",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    cursor: isPatientInactive ? "not-allowed" : "pointer",
                    opacity: isPatientInactive ? 0.55 : 1,
                  }}
                >
                  <FaFlag
                    size={12}
                    color={patient.flags.flag ? "#C29600" : "#8E8E93"}
                  />
                  Case Review
                </button>
                <button
                  type="button"
                  onClick={
                    isPatientInactive
                      ? undefined
                      : () => handleToggleFlag("warning")
                  }
                  disabled={isPatientInactive}
                  style={{
                    flex: 1,
                    height: "36px",
                    backgroundColor: patient.flags.warning
                      ? "#FDEDEC"
                      : "#FFFFFF",
                    border: patient.flags.warning
                      ? "1px solid #F5B7B1"
                      : "1px solid #EAECEF",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: patient.flags.warning ? "#E74C3C" : "#8E8E93",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    cursor: isPatientInactive ? "not-allowed" : "pointer",
                    opacity: isPatientInactive ? 0.55 : 1,
                  }}
                >
                  <FaExclamationTriangle
                    size={12}
                    color={patient.flags.warning ? "#E74C3C" : "#8E8E93"}
                  />
                  Safety Risk
                </button>
              </div>

              {/* Care Team */}
              <div
                style={{
                  borderTop: "1px solid #EAECEF",
                  paddingTop: "20px",
                  marginBottom: "20px",
                }}
              >
                <h5
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#8E8E93",
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    marginBottom: "16px",
                  }}
                >
                  Care Team
                </h5>
                <div style={{ marginBottom: "14px" }}>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Clinic
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#064260",
                      marginTop: "2px",
                    }}
                  >
                    {patient.clinic}
                  </div>
                </div>
                {_userRole !== "psychiatricConsultant" ||
                  (_userRole !== "careManager" && (
                    <>
                      <div style={{ marginBottom: "14px" }}>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          PCP
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {patient.pcp}
                        </div>
                      </div>
                    </>
                  ))}
                <div style={{ marginBottom: "14px" }}>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Care Manager
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#064260",
                      marginTop: "2px",
                    }}
                  >
                    {patient.cm}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Psychiatric Consultant
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#064260",
                      marginTop: "2px",
                    }}
                  >
                    {patient.psychiatricConsultant}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div
                style={{ borderTop: "1px solid #EAECEF", paddingTop: "20px" }}
              >
                <h5
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#8E8E93",
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                    marginBottom: "16px",
                  }}
                >
                  Contact Information
                </h5>
                <div style={{ marginBottom: "14px" }}>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Phone
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#064260",
                      marginTop: "2px",
                    }}
                  >
                    {patient.phone}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                    Email
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#064260",
                      marginTop: "2px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {patient.email}
                  </div>
                </div>
              </div>

              {/* App Access */}
              {showAppAccessSection && (
                <div
                  style={{
                    borderTop: "1px solid #EAECEF",
                    paddingTop: "20px",
                    marginTop: "20px",
                  }}
                >
                  <h5
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8E8E93",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      marginBottom: "16px",
                    }}
                  >
                    App Access
                  </h5>

                  <div
                    style={{
                      backgroundColor: "#F8F9FA",
                      borderRadius: "10px",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          App Link Status
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {appAccess?.status || "Not Invited"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          Linked Date
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {formatAppAccessDate(appAccess?.connectedDate)}
                        </div>
                      </div>
                      {appAccess?.status === "Linked" && (
                        <>
                          <div>
                            <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                              Notification Status
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color:
                                  appAccess?.notificationStatus ===
                                    "Email Failed" ||
                                  String(
                                    appAccess?.notificationStatus || "",
                                  ).includes("Failed")
                                    ? "#C0392B"
                                    : "#064260",
                                marginTop: "2px",
                              }}
                            >
                              {appAccess?.notificationStatus || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                              Notification Sent Date
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#064260",
                                marginTop: "2px",
                              }}
                            >
                              {formatAppAccessDateTime(
                                appAccess?.notificationSentAt,
                              )}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                              Last App Activity
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#064260",
                                marginTop: "2px",
                              }}
                            >
                              {formatAppAccessDateTime(
                                appAccess?.lastAppActivity,
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      {appAccess?.status === "Linked" &&
                        (appAccess?.linkedAppUserName ||
                          appAccess?.linkedAppUserDisplayId) && (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                              Linked App User
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#064260",
                                marginTop: "2px",
                              }}
                            >
                              {appAccess.linkedAppUserName || "App user"}
                              {appAccess.linkedAppUserDisplayId
                                ? ` • ${appAccess.linkedAppUserDisplayId}`
                                : ""}
                            </div>
                            {appAccess.linkSource && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#8E8E93",
                                  marginTop: "4px",
                                }}
                              >
                                Link Source: {appAccess.linkSource}
                              </div>
                            )}
                          </div>
                        )}
                      {appAccess?.matchIgnored && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                            Match Ignored
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#064260",
                              marginTop: "2px",
                            }}
                          >
                            Yes
                          </div>
                        </div>
                      )}
                    </div>

                    {appAccess?.status === "Linked" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowResendCareSupportModal(true)}
                          style={{
                            width: "100%",
                            height: "40px",
                            marginBottom: "8px",
                            backgroundColor: "#27B99A",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#FFFFFF",
                            cursor: "pointer",
                          }}
                        >
                          Resend Notification
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAppAccessDetailsModal(true)}
                          style={{
                            width: "100%",
                            height: "40px",
                            marginBottom: "8px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #EAECEF",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#064260",
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeactivateAppAccessModal(true)}
                          style={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "#E74C3C",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#FFFFFF",
                            cursor: "pointer",
                          }}
                        >
                          Disconnect Access
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowSendInviteModal(true)}
                          disabled={!patient.email}
                          style={{
                            width: "100%",
                            height: "40px",
                            marginBottom: "8px",
                            backgroundColor: "#27B99A",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#FFFFFF",
                            cursor: patient.email ? "pointer" : "not-allowed",
                            opacity: patient.email ? 1 : 0.6,
                          }}
                        >
                          {appAccess?.status === "Invite Sent" ||
                          appAccess?.status === "Pending Registration"
                            ? "Resend Invite"
                            : "Send Invite"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAppAccessDetailsModal(true)}
                          style={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #EAECEF",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#064260",
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Guardian Access — minors only (or existing guardian link) */}
              {showGuardianAccessSection && (
                <div
                  style={{
                    borderTop: "1px solid #EAECEF",
                    paddingTop: "20px",
                    marginTop: "20px",
                  }}
                >
                  <h5
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8E8E93",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      marginBottom: "16px",
                    }}
                  >
                    Guardian Access
                  </h5>

                  <div
                    style={{
                      backgroundColor: "#F8F9FA",
                      borderRadius: "10px",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          Status
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {guardianAccess?.status || "Not Linked"}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          Last Email
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {guardianAccess?.lastEmailType === "child_added"
                            ? "Child Added"
                            : guardianAccess?.lastEmailType === "welcome"
                              ? "Welcome"
                              : "—"}
                        </div>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={{ fontSize: "11px", color: "#8E8E93" }}>
                          Guardian
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#064260",
                            marginTop: "2px",
                          }}
                        >
                          {`${guardianAccess?.firstName || ""} ${guardianAccess?.lastName || ""}`.trim() ||
                            "—"}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#8E8E93",
                            marginTop: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {guardianAccess?.email || "No guardian email yet"}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowSendGuardianModal(true)}
                      style={{
                        width: "100%",
                        height: "40px",
                        backgroundColor: "#27B99A",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#FFFFFF",
                        cursor: "pointer",
                      }}
                    >
                      {guardianAccess?.suggestedEmailType === "child_added"
                        ? guardianAccess?.thisChildLinked
                          ? "Resend Child Added Email"
                          : "Send Child Added Email"
                        : guardianAccess?.thisChildLinked
                          ? "Resend Welcome Email"
                          : "Send Welcome Email"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ─── Right Content Panel ─── */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#F7F7F7",
              minHeight: "calc(100vh - 86px)",
              padding: "16px 20px 16px 0px",
            }}
          >
            {/* White Header Section */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #EAECEF",
                marginBottom: "20px",
              }}
            >
              {/* Main Title Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                  borderBottom: "1px solid #EAECEF",
                }}
              >
                <h2
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#064260",
                    margin: 0,
                    padding: "24px 36px 10px 36px",
                  }}
                >
                  {patient.name}
                </h2>
                {_userRole !== "admin" && (
                  <button
                    style={{
                      margin: "16px 24px 10px 0px",
                      padding: "8px 30px",
                      backgroundColor: "#27B99A",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "16px !important",
                      fontSize: "18px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    Add
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="7"
                      viewBox="0 0 10 7"
                      fill="none"
                    >
                      <path d="M0 0L5 6.21212L10 0H0Z" fill="white" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Tabs Row */}
              <div
                style={{
                  display: "flex",
                  gap: "32px",
                  marginBottom: "0px",
                  padding: "0px 36px 0px 36px",
                }}
              >
                {["Overview", "Activity", "Notes"].map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <div
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        paddingBottom: "14px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: isActive ? "#27B99A" : "#8E8E93",
                        cursor: "pointer",
                        borderBottom: isActive
                          ? "3px solid #27B99A"
                          : "3px solid transparent",
                        marginBottom: "-1px",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {tab}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gray Content Area */}
            <div
              style={{
                flex: 1,
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              {/* Tab Content Panels */}
              {activeTab === "Overview" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  {/* Assessment Summary Card Container */}
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #EAECEF",
                      borderRadius: "16px",
                      padding: "24px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#064260",
                        marginBottom: "20px",
                      }}
                    >
                      Assessment Summary
                    </h4>

                    <Row className="g-3">
                      {renderAssessmentCard(
                        "PHQ-9",
                        stats?.assessments?.phq9?.initial,
                        stats?.assessments?.phq9?.latest,
                      )}
                      {renderAssessmentCard(
                        "GAD-7",
                        stats?.assessments?.gad7?.initial,
                        stats?.assessments?.gad7?.latest,
                      )}
                    </Row>
                    <Row className="g-3 mt-3">
                      {renderAssessmentCard(
                        "SMFQ",
                        stats?.assessments?.smfq?.initial,
                        stats?.assessments?.smfq?.latest,
                      )}
                      {renderAssessmentCard(
                        "SCARED",
                        stats?.assessments?.scared?.initial,
                        stats?.assessments?.scared?.latest,
                      )}
                    </Row>
                  </div>

                  {/* Engagement Summary Card Container */}
                  <div
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #EAECEF",
                      borderRadius: "16px",
                      padding: "24px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#064260",
                        marginBottom: "20px",
                      }}
                    >
                      Engagement Summary
                    </h4>

                    <Row className="mb-2">
                      {renderEngagementItem(
                        "Enrollment Date",
                        stats?.engagement?.enrollmentDate,
                      )}
                      {renderEngagementItem(
                        "Initial Assessment Date",
                        stats?.engagement?.initialAssessmentDate,
                      )}
                      {renderEngagementItem(
                        "Discharge Date",
                        stats?.engagement?.dischargeDate,
                      )}
                      {renderEngagementItem(
                        "Days Since",
                        stats?.engagement?.daysSince !== null &&
                          stats?.engagement?.daysSince !== undefined
                          ? stats.engagement.daysSince
                          : "—",
                      )}
                    </Row>

                    <Row>
                      {renderEngagementItem(
                        "Last Follow-Up",
                        stats?.engagement?.lastFollowUp,
                      )}
                      {isPatientInactive ? (
                        renderEngagementItem(
                          "Next Follow-Up",
                          stats?.engagement?.nextFollowUp,
                        )
                      ) : (
                        renderEditableEngagementItem(
                          "Next Follow-Up",
                          "nextFollowUp",
                          stats?.engagement?.nextFollowUp,
                        )
                      )}
                      {renderEngagementItem(
                        "Last Case Review",
                        stats?.engagement?.lastCaseReview,
                      )}
                      {isPatientInactive ? (
                        renderEngagementItem(
                          "Next Case Review",
                          stats?.engagement?.nextCaseReview,
                        )
                      ) : (
                        renderEditableEngagementItem(
                          "Next Case Review",
                          "nextCaseReview",
                          stats?.engagement?.nextCaseReview,
                        )
                      )}
                    </Row>
                  </div>
                </div>
              )}

              {activeTab === "Activity" && (
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EAECEF",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#064260",
                          color: "#FFFFFF",
                          height: "48px",
                        }}
                      >
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "left",
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "left",
                          }}
                        >
                          Type
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          Minutes
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          PHQ-9
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          GAD-7
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          SMFQ
                        </th>
                        <th
                          style={{
                            padding: "14px 24px",
                            fontSize: "13px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          SCARED
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingActivities ? (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              padding: "32px 24px",
                              textAlign: "center",
                              color: "#8E8E93",
                              fontSize: "13px",
                            }}
                          >
                            Loading activities...
                          </td>
                        </tr>
                      ) : patientActivities.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              padding: "32px 24px",
                              textAlign: "center",
                              color: "#8E8E93",
                              fontSize: "13px",
                            }}
                          >
                            No activities found for this patient.
                          </td>
                        </tr>
                      ) : (
                        patientActivities.map((act, index) => {
                        const isEven = index % 2 === 1;
                        const renderVal = (val) =>
                          val === "-" || val === null || val === undefined || val === ""
                            ? "—"
                            : val;
                        return (
                          <tr
                            key={act.id || index}
                            style={{
                              backgroundColor: isEven ? "#F8FAFC" : "#FFFFFF",
                              borderBottom:
                                index === patientActivities.length - 1
                                  ? "none"
                                  : "1px solid #EAECEF",
                              height: "48px",
                            }}
                          >
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "left",
                              }}
                            >
                              {act.date}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#064260",
                                fontWeight: "500",
                                textAlign: "left",
                              }}
                            >
                              {act.activityType}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "center",
                              }}
                            >
                              {renderVal(act.duration)}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "center",
                              }}
                            >
                              {renderVal(act.phq9)}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "center",
                              }}
                            >
                              {renderVal(act.gad7)}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "center",
                              }}
                            >
                              {renderVal(act.smfq)}
                            </td>
                            <td
                              style={{
                                padding: "14px 24px",
                                fontSize: "13px",
                                color: "#444444",
                                textAlign: "center",
                              }}
                            >
                              {renderVal(act.scared)}
                            </td>
                          </tr>
                        );
                      })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "Notes" && (
                <div
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EAECEF",
                    borderRadius: "16px",
                    padding: "24px",
                  }}
                >
                  {loadingActivities ? (
                    <div style={{ color: "#8E8E93", fontSize: "14px" }}>
                      Loading notes...
                    </div>
                  ) : patientActivityNotes.length === 0 ? (
                    <div style={{ color: "#8E8E93", fontSize: "14px" }}>
                      No clinical notes saved for this patient.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      {patientActivityNotes.map((act) => (
                        <div
                          key={act.id}
                          style={{
                            borderBottom: "1px solid #EAECEF",
                            paddingBottom: "16px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#064260",
                              }}
                            >
                              {act.activityType}
                            </span>
                            <span style={{ fontSize: "12px", color: "#8E8E93" }}>
                              {act.date}
                              {act.careManager ? ` • ${act.careManager}` : ""}
                            </span>
                          </div>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "14px",
                              color: "#444444",
                              lineHeight: 1.6,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {act.note}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showSendInviteModal && (
        <SendCoCMInviteModal
          patient={patient}
          isResend={
            appAccess?.status === "Invite Sent" ||
            appAccess?.status === "Pending Registration"
          }
          onClose={() => !inviteSubmitting && setShowSendInviteModal(false)}
          onConfirm={handleSendInvite}
          isSubmitting={inviteSubmitting}
        />
      )}

      {showSendGuardianModal && (
        <SendGuardianAccessModal
          patient={patient}
          guardianAccess={guardianAccess}
          onClose={() =>
            !guardianInviteSubmitting && setShowSendGuardianModal(false)
          }
          onConfirm={handleSendGuardianInvite}
          isSubmitting={guardianInviteSubmitting}
        />
      )}

      {showResendCareSupportModal && (
        <ResendCareSupportNotificationModal
          patient={patient}
          appAccess={appAccess}
          onClose={() =>
            !careSupportResendSubmitting && setShowResendCareSupportModal(false)
          }
          onConfirm={handleResendCareSupportNotification}
          isSubmitting={careSupportResendSubmitting}
        />
      )}

      {showAppAccessDetailsModal && (
        <AppAccessDetailsModal
          patient={patient}
          appAccess={appAccess}
          onClose={() => setShowAppAccessDetailsModal(false)}
        />
      )}

      {showDeactivateAppAccessModal && (
        <DeactivateAppAccessModal
          patient={patient}
          onClose={() => !deactivateSubmitting && setShowDeactivateAppAccessModal(false)}
          onConfirm={handleDeactivateAppAccess}
          isSubmitting={deactivateSubmitting}
        />
      )}

      {showDeactivatePatientModal && patient && (
        <DeactivatePatientModal
          patient={patient}
          onClose={() => !patientDeactivating && setShowDeactivatePatientModal(false)}
          onConfirm={handleDeactivatePatient}
          isSubmitting={patientDeactivating}
        />
      )}
    </>
  );
};

export default PatientInfo;
